import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Grid, 
  TextField, 
  Button, 
  IconButton, 
  Tooltip,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  Divider,
  Card,
  CardContent,
  InputAdornment,
  Chip,
  Collapse,
  MenuItem,
} from '@mui/material';
import { ChromePicker } from 'react-color';
import { useFormContext, Controller } from 'react-hook-form';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ImageIcon from '@mui/icons-material/Image';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import ShareIcon from '@mui/icons-material/Share';
import ImageUpload from './common/ImageUpload';
import LinkIcon from '@mui/icons-material/Link';
import { useBrand } from '../../brands/BrandProvider';
import { useBrandRegistrationCopy } from '../../brands/registrationCopy';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`event-page-tabpanel-${index}`}
      aria-labelledby={`event-page-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

interface EventPageFormProps {
  previewMode: boolean;
  togglePreviewMode: () => void;
}

const EventPageForm: React.FC<EventPageFormProps> = ({ previewMode, togglePreviewMode }) => {
  const brand = useBrand();
  const t = useBrandRegistrationCopy();
  const { control, watch, setValue, formState: { errors } } = useFormContext();
  const [tabValue, setTabValue] = useState(0);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [colorPickerType, setColorPickerType] = useState<'primary' | 'secondary'>('primary');
  
  // Observar valores para preview
  const title = watch('title') || 'Título do Evento';
  const description = watch('description') || 'Descrição do evento...';
  const location = watch('location') || 'Local do Evento';
  const startDate = watch('startDate');
  const endDate = watch('endDate');
  const coverImage = watch('coverImage');
  const contactEmail = watch('contactEmail') || '';
  const contactPhone = watch('contactPhone') || '';
  const showMap = watch('showMap') !== undefined ? watch('showMap') : true;
  const showSocialShare = watch('showSocialShare') !== undefined ? watch('showSocialShare') : true;
  const customTheme = watch('customTheme') || {
    primaryColor: '#3f51b5',
    secondaryColor: '#f50057',
    fontFamily: 'Roboto'
  };
  
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };
  
  const handleColorPickerOpen = (type: 'primary' | 'secondary') => {
    setColorPickerType(type);
    setShowColorPicker(true);
  };
  
  const handleColorChange = (color: any) => {
    setValue(`customTheme.${colorPickerType}Color`, color.hex);
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          Página do Evento
          <Tooltip title="Personalize como a página do seu evento será exibida para os participantes">
            <IconButton size="small" sx={{ ml: 1 }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Typography>
        
        <Button
          variant="outlined"
          startIcon={previewMode ? <VisibilityOffIcon /> : <VisibilityIcon />}
          onClick={togglePreviewMode}
        >
          {previewMode ? 'Ocultar Preview' : 'Mostrar Preview'}
        </Button>
      </Box>
      
      <Grid container spacing={3}>
        <Grid item xs={12} md={previewMode ? 6 : 12}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="configurações da página">
              <Tab label="Imagens" />
              <Tab label="Contato" />
              <Tab label="Aparência" />
              <Tab label="Template" />
              <Tab label={t('Link pós-inscrição')} />
              <Tab label="Ativação" />
            </Tabs>
          </Box>
          
          <TabPanel value={tabValue} index={0}>
            <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Imagem de Capa
              </Typography>
              
              <Controller
                name="coverImage"
                control={control}
                render={({ field }) => (
                  <ImageUpload
                    value={field.value}
                    onChange={(file) => field.onChange(file)}
                    label="Arraste uma imagem ou clique para selecionar"
                    helperText="Recomendado: 1200 x 600px, máximo 2MB"
                    error={!!errors.coverImage}
                    errorMessage={errors.coverImage?.message as string}
                    enableCrop={true}
                    cropAspectRatio={2}
                    autoCompress={true}
                  />
                )}
              />
            </Paper>
            
            <Paper elevation={0} variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Galeria de Imagens
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Adicione até 6 imagens para mostrar mais detalhes do seu evento
              </Typography>
              
              <Controller
                name="galleryImages"
                control={control}
                render={({ field }) => (
                  <ImageUpload
                    value={field.value}
                    onChange={(files) => field.onChange(files)}
                    label="Arraste imagens ou clique para selecionar"
                    helperText="Até 6 imagens, máximo 2MB cada"
                    multiple
                    maxFiles={6}
                    error={!!errors.galleryImages}
                    errorMessage={errors.galleryImages?.message as string}
                  />
                )}
              />
            </Paper>
          </TabPanel>
          
          <TabPanel value={tabValue} index={1}>
            <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Informações de Contato
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Controller
                    name="contactEmail"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Email de Contato"
                        placeholder={brand.copy.eventForm.contactEmailPlaceholder}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailIcon />
                            </InputAdornment>
                          ),
                        }}
                        error={!!errors.contactEmail}
                        helperText={errors.contactEmail?.message as string}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="contactPhone"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        fullWidth
                        label="Telefone de Contato"
                        placeholder="Ex: (11) 98765-4321"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <PhoneIcon />
                            </InputAdornment>
                          ),
                        }}
                        error={!!errors.contactPhone}
                        helperText={errors.contactPhone?.message as string}
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>
            
            <Paper elevation={0} variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Opções de Exibição
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="showMap"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Exibir mapa do local"
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <Controller
                    name="showSocialShare"
                    control={control}
                    defaultValue={true}
                    render={({ field }) => (
                      <FormControlLabel
                        control={
                          <Switch
                            checked={field.value}
                            onChange={(e) => field.onChange(e.target.checked)}
                          />
                        }
                        label="Exibir botões de compartilhamento"
                      />
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>
          </TabPanel>
          
          <TabPanel value={tabValue} index={2}>
            <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Cores do Tema
              </Typography>
              
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Cor Principal
                    </Typography>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: customTheme.primaryColor,
                          border: '1px solid #ddd',
                          mr: 2,
                          cursor: 'pointer'
                        }}
                        onClick={() => handleColorPickerOpen('primary')}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {customTheme.primaryColor}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" gutterBottom>
                      Cor Secundária
                    </Typography>
                    <Box 
                      sx={{ 
                        display: 'flex',
                        alignItems: 'center'
                      }}
                    >
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: 1,
                          bgcolor: customTheme.secondaryColor,
                          border: '1px solid #ddd',
                          mr: 2,
                          cursor: 'pointer'
                        }}
                        onClick={() => handleColorPickerOpen('secondary')}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {customTheme.secondaryColor}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              </Grid>
              
              <Collapse in={showColorPicker}>
                <Box sx={{ mt: 2, mb: 2 }}>
                  <Typography variant="body2" gutterBottom>
                    Selecione a cor {colorPickerType === 'primary' ? 'principal' : 'secundária'}:
                  </Typography>
                  <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                    <ChromePicker
                      color={colorPickerType === 'primary' ? customTheme.primaryColor : customTheme.secondaryColor}
                      onChange={handleColorChange}
                      disableAlpha
                    />
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                    <Button size="small" onClick={() => setShowColorPicker(false)}>
                      Fechar
                    </Button>
                  </Box>
                </Box>
              </Collapse>
            </Paper>
            
            <Paper elevation={0} variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Fonte
              </Typography>
              
              <Controller
                name="customTheme.fontFamily"
                control={control}
                defaultValue="Roboto"
                render={({ field }) => (
                  <TextField
                    select
                    fullWidth
                    label="Família de Fonte"
                    {...field}
                  >
                    <MenuItem value="Roboto">Roboto (Padrão)</MenuItem>
                    <MenuItem value="Montserrat">Montserrat</MenuItem>
                    <MenuItem value="Lato">Lato</MenuItem>
                    <MenuItem value="Open Sans">Open Sans</MenuItem>
                    <MenuItem value="Playfair Display">Playfair Display</MenuItem>
                  </TextField>
                )}
              />
            </Paper>
          </TabPanel>
          
          <TabPanel value={tabValue} index={3}>
            <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Template da Página
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Escolha o template visual que será usado na página pública do evento
              </Typography>
              
              <Controller
                name="pageTemplate"
                control={control}
                defaultValue="default"
                render={({ field }) => (
                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: field.value === 'default' ? 2 : 1,
                          borderColor: field.value === 'default' ? 'primary.main' : 'divider',
                          bgcolor: field.value === 'default' ? 'action.selected' : 'background.paper',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover'
                          },
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => field.onChange('default')}
                      >
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Padrão
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Template padrão da plataforma com layout moderno e responsivo
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: field.value === 'acampamento' ? 2 : 1,
                          borderColor: field.value === 'acampamento' ? 'primary.main' : 'divider',
                          bgcolor: field.value === 'acampamento' ? 'action.selected' : 'background.paper',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover'
                          },
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => field.onChange('acampamento')}
                      >
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Acampamento
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Template ideal para eventos ao ar livre, acampamentos e atividades na natureza
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    
                    <Grid item xs={12} sm={6} md={4}>
                      <Card
                        sx={{
                          cursor: 'pointer',
                          border: field.value === 'cinema' ? 2 : 1,
                          borderColor: field.value === 'cinema' ? 'primary.main' : 'divider',
                          bgcolor: field.value === 'cinema' ? 'action.selected' : 'background.paper',
                          '&:hover': {
                            borderColor: 'primary.main',
                            bgcolor: 'action.hover'
                          },
                          transition: 'all 0.2s ease'
                        }}
                        onClick={() => field.onChange('cinema')}
                      >
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Cinema
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Template perfeito para eventos culturais, cinema, teatro e apresentações artísticas
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                )}
              />
            </Paper>
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            <Paper elevation={0} variant="outlined" sx={{ p: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('Link pós-inscrição (opcional)')}
              </Typography>

              <Typography variant="body2" color="text.secondary" paragraph>
                {t('Após concluir a inscrição, o participante verá um botão para acessar este link.')}
                Você pode usar para grupo de WhatsApp, página com instruções, formulário extra, etc.
              </Typography>

              <Controller
                name="postRegistrationUrl"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label={t('URL pós-inscrição')}
                    placeholder="https://exemplo.com/grupo-ou-instrucoes"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkIcon />
                        </InputAdornment>
                      ),
                    }}
                    error={!!errors.postRegistrationUrl}
                    helperText={
                      (errors.postRegistrationUrl?.message as string) ||
                      'Este campo é opcional. Lembre-se de usar http:// ou https:// no início do link.'
                    }
                  />
                )}
              />
            </Paper>
          </TabPanel>
          
          <TabPanel value={tabValue} index={5}>
            <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 3 }}>
              <Typography variant="subtitle1" gutterBottom>
                Status do Evento
              </Typography>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                Escolha como você quer que seu evento seja exibido:
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Controller
                    name="status"
                    control={control}
                    defaultValue="published"
                    render={({ field }) => (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Button
                          variant={field.value === 'paused' ? 'contained' : 'outlined'}
                          color={field.value === 'paused' ? 'warning' : 'inherit'}
                          onClick={() => field.onChange('paused')}
                          sx={{ justifyContent: 'flex-start', p: 2 }}
                        >
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="subtitle2">Pausado</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Pausa o evento temporariamente. Não será exibido publicamente.
                            </Typography>
                          </Box>
                        </Button>
                        
                        <Button
                          variant={field.value === 'test' ? 'contained' : 'outlined'}
                          color={field.value === 'test' ? 'secondary' : 'inherit'}
                          onClick={() => field.onChange('test')}
                          sx={{ justifyContent: 'flex-start', p: 2 }}
                        >
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="subtitle2">Modo Teste</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Evento ativo para testes. Não aparece na tela principal nem na API do app.
                            </Typography>
                          </Box>
                        </Button>
                        
                        <Button
                          variant={field.value === 'published' ? 'contained' : 'outlined'}
                          color={field.value === 'published' ? 'success' : 'inherit'}
                          onClick={() => field.onChange('published')}
                          sx={{ justifyContent: 'flex-start', p: 2 }}
                        >
                          <Box sx={{ textAlign: 'left' }}>
                            <Typography variant="subtitle2">Publicado</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Evento ativo e visível para todos os usuários.
                            </Typography>
                          </Box>
                        </Button>
                      </Box>
                    )}
                  />
                </Grid>
              </Grid>
            </Paper>
          </TabPanel>
        </Grid>
        
        {previewMode && (
          <Grid item xs={12} md={6}>
            <Paper 
              elevation={3} 
              sx={{ 
                p: 0, 
                overflow: 'hidden',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                fontFamily: customTheme.fontFamily
              }}
            >
              <Box sx={{ position: 'relative' }}>
  {(typeof coverImage === 'string' || coverImage instanceof File) ? (
    <Box
      component="img"
      src={
        typeof coverImage === 'string'
          ? coverImage
          : URL.createObjectURL(coverImage)
      }
      alt={title}
      sx={{
        width: '100%',
        height: 200,
        objectFit: 'cover',
      }}
    />
  ) : (
    <Box
      sx={{
        width: '100%',
        height: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.200',
      }}
    >
      <ImageIcon sx={{ fontSize: 60, color: 'grey.400' }} />
    </Box>
  )}

  <Box
    sx={{
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      p: 2,
      background: 'linear-gradient(transparent, rgba(0,0,0,0.7))',
      color: 'white',
    }}
  >
    <Typography variant="h5" component="h1" fontWeight="bold" fontFamily="inherit">
      {title}
    </Typography>
  </Box>
</Box>
              
              <Box sx={{ p: 3, flexGrow: 1 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" paragraph fontFamily="inherit">
                    {description}
                  </Typography>
                </Box>
                
                <Divider sx={{ my: 2 }} />
                
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" fontFamily="inherit">
                      Data e Hora
                    </Typography>
                    <Typography variant="body2" fontFamily="inherit">
                      {startDate?.toLocaleString('pt-BR')}
                      {endDate && ` até ${endDate.toLocaleString('pt-BR')}`}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" color="text.secondary" fontFamily="inherit">
                      Local
                    </Typography>
                    <Typography variant="body2" fontFamily="inherit">
                      {location}
                    </Typography>
                  </Grid>
                </Grid>
                
                <Box sx={{ mb: 3 }}>
                  <Button 
                    variant="contained" 
                    fullWidth
                    sx={{ 
                      bgcolor: customTheme.primaryColor,
                      '&:hover': {
                        bgcolor: customTheme.primaryColor,
                        filter: 'brightness(0.9)'
                      }
                    }}
                  >
                    Inscrever-se
                  </Button>
                </Box>
                
                {(contactEmail || contactPhone) && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom fontFamily="inherit">
                      Contato
                    </Typography>
                    
                    {contactEmail && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <EmailIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" fontFamily="inherit">
                          {contactEmail}
                        </Typography>
                      </Box>
                    )}
                    
                    {contactPhone && (
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <PhoneIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2" fontFamily="inherit">
                          {contactPhone}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
                
                {showMap && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" gutterBottom fontFamily="inherit">
                      Localização
                    </Typography>
                    <Box
                      sx={{
                        width: '100%',
                        height: 150,
                        bgcolor: 'grey.200',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1,
                      }}
                    >
                      <LocationOnIcon sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2" color="text.secondary" fontFamily="inherit">
                        Mapa do Local
                      </Typography>
                    </Box>
                  </Box>
                )}
                
                {showSocialShare && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                    <Chip 
                      icon={<ShareIcon />} 
                      label="Compartilhar" 
                      variant="outlined" 
                      sx={{ 
                        borderColor: customTheme.secondaryColor,
                        color: customTheme.secondaryColor
                      }} 
                    />
                  </Box>
                )}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
      
      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          {t('Dica: Uma página de evento bem projetada, com imagens atrativas e informações claras, aumenta significativamente as taxas de inscrição.')}
        </Typography>
      </Box>
    </Box>
  );
};

export default EventPageForm;
